import React, { useState } from "react";
import "./AuthModal.css";

const AuthModal = ({
  isOpen,
  onClose,
  onSuccess,
  mode: initialMode = "login",
  returnUrl = "",
  showGuestOption = false,
  onGuestCheckout = null,
}) => {
  const [mode, setMode] = useState(initialMode); // 'login', 'register', or 'verify-otp'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [tempLoginData, setTempLoginData] = useState(null); // Store login response until OTP verified
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phone: "",
  });

  const [passwordStrength, setPasswordStrength] = useState({
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
    isLongEnough: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccessMessage("");

    // Check password strength for registration
    if (name === "password" && mode === "register") {
      checkPasswordStrength(value);
    }
  };

  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      isLongEnough: password.length >= 8,
    });
  };

  const isPasswordValid = () => {
    const { hasUpper, hasLower, hasNumber, hasSpecial, isLongEnough } =
      passwordStrength;
    const count = [hasUpper, hasLower, hasNumber, hasSpecial].filter(
      Boolean,
    ).length;
    return isLongEnough && count >= 3;
  };

  const parseResponseSafely = async (response) => {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      return response.json();
    }
    return {};
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      if (mode === "register") {
        // Validate password
        if (!isPasswordValid()) {
          setError(
            "Password must be at least 8 characters with 3 types: uppercase, lowercase, number, special character",
          );
          setLoading(false);
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          setError("Passwords do not match");
          setLoading(false);
          return;
        }

        if (!smsConsent) {
          setError("Please agree to receive SMS notifications");
          setLoading(false);
          return;
        }

        if (!agreeToTerms) {
          setError("Please agree to the Terms of Service and Privacy Policy");
          setLoading(false);
          return;
        }

        // Register
        const registerResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/customer/register`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
              firstName: formData.firstName,
              lastName: formData.lastName,
              phone: formData.phone,
              role: "R5", // Customer
              smsConsent,
              rememberMe: rememberMe,
            }),
          },
        );

        const registerData = await parseResponseSafely(registerResponse);
        if (!registerResponse.ok) {
          throw new Error(registerData.message || "Registration failed");
        }

        // Customer registration now requires email verification; return to login mode.
        setMode("login");
        setSuccessMessage(
          "Account created. Please verify your email via the link sent to your inbox, then sign in.",
        );
        setLoading(false);
        return; // Don't save token yet, wait for OTP
      } else {
        // Login
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/login`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
              rememberMe: rememberMe,
            }),
          },
        );

        const data = await parseResponseSafely(response);
        if (!response.ok) {
          throw new Error(data.message || "Login failed");
        }

        // Store login data temporarily and show OTP verification
        setTempLoginData(data);
        setMode("verify-otp");
        setLoading(false);
        return; // Don't save token yet, wait for OTP
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!otpCode || otpCode.length !== 6) {
        throw new Error("Please enter a valid 6-digit code");
      }

      // Verify OTP and migrate cart if guest had items
      const sessionId = localStorage.getItem("ogestyle_cart_session");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/verify-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(sessionId && { "x-session-id": sessionId }),
          },
          body: JSON.stringify({
            email: formData.email,
            code: otpCode,
          }),
        },
      );

      const data = await parseResponseSafely(response);
      if (!response.ok) {
        throw new Error(data.message || "Verification failed");
      }

      // OTP verified successfully, now save token
      if (tempLoginData && data?.token) {
        localStorage.setItem("ogestyle_token", data.token);
        if (data.expiresAt) {
          localStorage.setItem(
            "ogestyle_token_expires",
            data.expiresAt,
          );
        }
        if (rememberMe) {
          localStorage.setItem("ogestyle_remember_me", "true");
        }

        if (data?.user?.role) {
          localStorage.setItem("ogestyle_role", data.user.role);
        }
        if (Array.isArray(data?.user?.roles)) {
          localStorage.setItem("ogestyle_roles", JSON.stringify(data.user.roles));
        }
        if (data?.user?.hasCompletedSetup !== undefined) {
          localStorage.setItem(
            "ogestyle_hasCompletedSetup",
            data.user.hasCompletedSetup !== false ? "true" : "false",
          );
        }

        // Clear guest session ID after successful login (cart has been migrated on backend)
        localStorage.removeItem("ogestyle_cart_session");

        // Dispatch custom event to notify other components (like header and cart) of auth state change
        window.dispatchEvent(new Event("authStateChanged"));
        window.dispatchEvent(new Event("cartMigrated")); // Signal cart to refresh

        onSuccess?.(data);
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError("");
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: formData.email,
            resendCode: true,
          }),
        },
      );

      const data = await parseResponseSafely(response);
      if (!response.ok) {
        throw new Error(data.message || "Failed to resend code");
      }

      setError(""); // Clear any errors
      alert("Verification code resent! Check your email.");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    const API_URL =
      process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
    const providerLower = provider.toLowerCase();

    // Open OAuth popup
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `${API_URL}/api/auth/${providerLower}`,
      `${provider} Login`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
    );

    // Listen for OAuth callback
    const checkPopup = setInterval(() => {
      try {
        if (popup.closed) {
          clearInterval(checkPopup);
          return;
        }

        // Check if popup redirected to success URL
        const popupUrl = popup.location.href;
        if (popupUrl.includes("/auth/success")) {
          const url = new URL(popupUrl);
          const token = url.searchParams.get("token");

          if (token) {
            // Save token
            localStorage.setItem("ogestyle_token", token);

            // Close popup
            popup.close();
            clearInterval(checkPopup);

            // Call success callback
            if (onSuccess) onSuccess();
            onClose();
          }
        } else if (popupUrl.includes("/auth/error")) {
          popup.close();
          clearInterval(checkPopup);
          setError(`${provider} authentication failed. Please try again.`);
        }
      } catch (err) {
        // Cross-origin errors are expected until redirect
      }
    }, 500);

    // Timeout after 5 minutes
    setTimeout(() => {
      clearInterval(checkPopup);
      if (popup && !popup.closed) {
        popup.close();
      }
    }, 300000);
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay">
      <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h2>
            {mode === "verify-otp"
              ? "Verify Your Email 📧"
              : mode === "login"
                ? "Welcome Back! 👋"
                : "Create Your Account"}
          </h2>
          <p className="auth-subtitle">
            {mode === "verify-otp"
              ? `We've sent a 6-digit code to ${formData.email}`
              : mode === "login"
                ? "Sign in to continue your booking"
                : "Join OGESTYLE to book beauty & wellness services"}
          </p>
        </div>

        <div className="auth-modal-body">
          {mode === "verify-otp" ? (
            /* OTP Verification Form */
            <form onSubmit={handleVerifyOTP} className="auth-form">
              <div className="otp-input-container">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="6"
                  placeholder="Enter 6-digit code"
                  value={otpCode}
                  onChange={(e) =>
                    setOtpCode(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  required
                  className="auth-input otp-input"
                  autoFocus
                />
              </div>

              {error && <div className="auth-error">{error}</div>}

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading || otpCode.length !== 6}
              >
                {loading ? "Verifying..." : "Verify & Continue"}
              </button>

              <div className="otp-resend-section">
                <p>Didn't receive the code?</p>
                <button
                  type="button"
                  className="resend-btn"
                  onClick={handleResendOTP}
                  disabled={loading}
                >
                  Resend Code
                </button>
              </div>

              <button
                type="button"
                className="back-to-login-btn"
                onClick={() => {
                  setMode("login");
                  setOtpCode("");
                  setError("");
                  setTempLoginData(null);
                }}
              >
                ← Back to Login
              </button>
            </form>
          ) : (
            <>
              {/* Social Login Buttons - TEMPORARILY HIDDEN */}
              {false && (
                <>
                  <div className="social-login-section">
                    <button
                      type="button"
                      className="social-login-btn google"
                      onClick={() => handleSocialLogin("Google")}
                    >
                      <svg
                        className="social-icon"
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                      >
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      Google
                    </button>
                    <button
                      type="button"
                      className="social-login-btn facebook"
                      onClick={() => handleSocialLogin("Facebook")}
                    >
                      <svg
                        className="social-icon"
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                      >
                        <path
                          fill="#1877F2"
                          d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                        />
                      </svg>
                      Facebook
                    </button>
                    <button
                      type="button"
                      className="social-login-btn apple"
                      onClick={() => handleSocialLogin("Apple")}
                    >
                      <svg
                        className="social-icon"
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                      >
                        <path
                          fill="#000000"
                          d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                        />
                      </svg>
                      Apple
                    </button>
                  </div>

                  <div className="auth-divider">
                    <span>or</span>
                  </div>
                </>
              )}

              {/* Email/Password Form */}
              <form onSubmit={handleSubmit} className="auth-form">
                {mode === "register" && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="modal-firstName">First Name *</label>
                        <input
                          id="modal-firstName"
                          type="text"
                          name="firstName"
                          placeholder="John"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="auth-input"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="modal-lastName">Last Name *</label>
                        <input
                          id="modal-lastName"
                          type="text"
                          name="lastName"
                          placeholder="Doe"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="auth-input"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="modal-phone">Phone Number *</label>
                      <input
                        id="modal-phone"
                        type="tel"
                        name="phone"
                        placeholder="+1 (555) 123-4567"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="auth-input"
                      />
                    </div>
                  </>
                )}
                <div className="form-group">
                  <label htmlFor="modal-email">
                    {mode === "register" ? "Email Address *" : "Email"}
                  </label>
                  <input
                    id="modal-email"
                    type="email"
                    name="email"
                    placeholder={mode === "register" ? "john@example.com" : "Email"}
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="auth-input"
                  />
                </div>

                <div className={mode === "register" ? "form-row" : "form-group"}>
                  <div className="form-group">
                    <label htmlFor="modal-password">
                      {mode === "register" ? "Password *" : "Password"}
                    </label>
                    <input
                      id="modal-password"
                      type="password"
                      name="password"
                      placeholder={mode === "register" ? "At least 8 characters" : "Password"}
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className="auth-input"
                    />
                  </div>

                  {mode === "register" && (
                    <div className="form-group">
                      <label htmlFor="modal-confirmPassword">Confirm Password *</label>
                      <input
                        id="modal-confirmPassword"
                        type="password"
                        name="confirmPassword"
                        placeholder="Re-enter your password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        required
                        className="auth-input"
                      />
                    </div>
                  )}
                </div>

                {mode === "register" && formData.password && (
                  <div className="password-strength">
                    <div className="strength-item">
                      <span
                        className={
                          passwordStrength.isLongEnough ? "valid" : "invalid"
                        }
                      >
                        {passwordStrength.isLongEnough ? "✓" : "○"}
                      </span>
                      At least 8 characters
                    </div>
                    <div className="strength-item">
                      <span
                        className={
                          passwordStrength.hasUpper ? "valid" : "invalid"
                        }
                      >
                        {passwordStrength.hasUpper ? "✓" : "○"}
                      </span>
                      One uppercase letter
                    </div>
                    <div className="strength-item">
                      <span
                        className={
                          passwordStrength.hasLower ? "valid" : "invalid"
                        }
                      >
                        {passwordStrength.hasLower ? "✓" : "○"}
                      </span>
                      One lowercase letter
                    </div>
                    <div className="strength-item">
                      <span
                        className={
                          passwordStrength.hasNumber ? "valid" : "invalid"
                        }
                      >
                        {passwordStrength.hasNumber ? "✓" : "○"}
                      </span>
                      One number
                    </div>
                    <div className="strength-item">
                      <span
                        className={
                          passwordStrength.hasSpecial ? "valid" : "invalid"
                        }
                      >
                        {passwordStrength.hasSpecial ? "✓" : "○"}
                      </span>
                      One special character (!@#$%...)
                    </div>
                    <p className="strength-note">
                      At least 3 out of 4 character types required
                    </p>
                  </div>
                )}

                {mode === "register" && (
                  <div className="consent-card">
                    <label htmlFor="modal-smsConsent" className="consent-label">
                      <input
                        type="checkbox"
                        id="modal-smsConsent"
                        checked={smsConsent}
                        onChange={(e) => setSmsConsent(e.target.checked)}
                        className="consent-checkbox"
                        required
                      />
                      <span>
                        I agree to receive SMS notifications from OGESTYLE about my appointments,
                        including booking confirmations, reminders, and updates. Message frequency
                        may vary. Message and data rates may apply. Text HELP for support. Reply STOP
                        to opt-out anytime. <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                      </span>
                    </label>
                  </div>
                )}

                {mode === "register" && (
                  <label htmlFor="modal-terms" className="terms-label">
                    <input
                      type="checkbox"
                      id="modal-terms"
                      checked={agreeToTerms}
                      onChange={(e) => setAgreeToTerms(e.target.checked)}
                      className="consent-checkbox"
                      required
                    />
                    <span>
                      I agree <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                      {" "}and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                    </span>
                  </label>
                )}

                {/* Remember Me Checkbox */}
                <div
                  className="remember-me-container"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    style={{ cursor: "pointer" }}
                  />
                  <label
                    htmlFor="rememberMe"
                    style={{
                      fontSize: "14px",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    Remember me for 30 days
                  </label>
                </div>

                {successMessage && <div className="auth-success">{successMessage}</div>}
                {error && <div className="auth-error">{error}</div>}
                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={
                    loading || (mode === "register" && !isPasswordValid())
                  }
                >
                  {loading
                    ? "Please wait..."
                    : mode === "login"
                      ? "Sign In"
                      : "Create Account"}
                </button>
                {/* Phase C11.5: Guest Checkout Option */}
                {showGuestOption && onGuestCheckout && mode === "login" && (
                  <div className="guest-checkout-option">
                    <div className="divider">
                      <span>OR</span>
                    </div>
                    <button
                      type="button"
                      onClick={onGuestCheckout}
                      className="guest-checkout-btn"
                    >
                      Continue as Guest
                    </button>
                    <p className="guest-notice-small">
                      No account required. Quick and easy checkout.
                    </p>
                  </div>
                )}{" "}
              </form>

              <div className="auth-toggle">
                {mode === "login" ? (
                  <p>
                    Don't have an account?{" "}
                    <button
                      type="button"
                      onClick={() => setMode("register")}
                      className="auth-toggle-btn"
                    >
                      Sign Up
                    </button>
                  </p>
                ) : (
                  <p>
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => setMode("login")}
                      className="auth-toggle-btn"
                    >
                      Sign In
                    </button>
                  </p>
                )}
              </div>

              {/* Account Benefits */}
              {mode === "register" && (
                <div className="auth-benefits">
                  <p className="benefits-title">Account Benefits:</p>
                  <ul className="benefits-list">
                    <li>✅ Save payment methods</li>
                    <li>✅ View booking history</li>
                    <li>✅ Get exclusive offers</li>
                    <li>✅ Manage packages</li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
